import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';
import { SelectItem } from 'primereact/selectitem';
import { Typography } from '../Typography';
import { InfoTooltip } from "../Tooltip/InfoTooltip";
import SelectIcon from '../../assets/select.svg';
import SelectOpenIcon from '../../assets/selectOpen.svg';

interface FormMultiSelectProps extends FieldProps<string[], FieldRenderProps<string[]>> {
  items: SelectItem[];
  placeholder?: string;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  initialValue?: string[];
  selectionLimit?: number;
}

export const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
  label,
  tooltip,
  items,
  placeholder,
  initialValue,
  selectionLimit = 3,
  ...fieldProps
}) => (
  <Field
    {...fieldProps}
    type="multi-select"
    initialValue={initialValue}
    render={({ input }) => (
      <InputMultiSelect
        input={input}
        items={items}
        placeholder={placeholder}
        label={label}
        tooltip={tooltip}
        selectionLimit={selectionLimit}
      />
    )}
  />
);

interface InputMultiSelectProps {
  input: FieldRenderProps<string[]>['input'];
  items: SelectItem[];
  placeholder?: string;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  selectionLimit?: number;
}

const InputMultiSelect: React.FC<InputMultiSelectProps> = ({
  input,
  items,
  placeholder,
  label,
  tooltip,
  selectionLimit = 3
}) => {
  const [selected, setSelected] = useState<string[]>(input.value || []);
  const [open, setOpen] = useState(false);

  // Sync local state if form value changes (e.g., reset)
  useEffect(() => {
    if (JSON.stringify(input.value) !== JSON.stringify(selected)) {
      setSelected(input.value || []);
    }
  }, [input.value]);

  const safeId = `select-tooltip-${String(label ?? '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')}`;

  const toggleItem = (value: string) => {
    setSelected(prev => {
      let newSelected: string[];
      if (prev.includes(value)) {
        newSelected = prev.filter(v => v !== value);
      } else if (prev.length < selectionLimit) {
        newSelected = [...prev, value];
      } else {
        newSelected = prev;
      }

      // update form value here
      input.onChange(newSelected);
      return newSelected;
    });
  };

  return (
    <Container>
      {label && (
        <Label>
          <Typography variant="body6">
            <label>{label}</label>
          </Typography>
          {tooltip && <InfoTooltip name={safeId} tooltip={tooltip} />}
        </Label>
      )}
      <Box>
        <StyledInput
          placeholder={placeholder}
          value={selected.map(v => items.find(i => i.value === v)?.label).join(', ')}
          readOnly
          onClick={() => setOpen(prev => !prev)}
        />
        <Icon>{open ? <SelectOpenIcon /> : <SelectIcon />}</Icon>
      </Box>
      {open && (
        <Dropdown>
          {items.map(item => (
            <Option
              key={item.value}
              selected={selected.includes(item.value)}
              onClick={() => toggleItem(item.value)}
            >
              {item.label}
            </Option>
          ))}
        </Dropdown>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  border-radius: 10px;
  padding: 14px;
  cursor: pointer;
  outline: none;
  border: 1px solid #E9E9E9;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #ccc;
  border-radius: 10px;
  margin-top: 4px;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const Option = styled.div<{ selected: boolean }>`
  padding: 8px;
  background: ${({ selected }) => (selected ? '#e0f0ff' : 'white')};
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }
`;

const Label = styled.div`
  display: flex;
  gap: 4px;
`;

const Box = styled.div`
  width: 100%;
  position: relative;
`;

const Icon = styled.div`
  position: absolute;
  right: 12px;
  top: 16px;
  cursor: pointer;
  color: #6777FF;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;
